import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Link,
  Navigate,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, BadgePercent, CheckCircle2, Lock, Sparkles, X } from 'lucide-react';
import { loadStripe, type Stripe as StripeType } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { Button } from '@/client/components/ui/button';
import { api, ApiError } from '@/client/lib/api';
import { useMe } from '@/client/hooks/useMe';
import { isLocale, type Locale } from '@/client/i18n';
import { cn } from '@/client/lib/utils';
import type { PublicCourseDetailResponse } from '@/client/types/courses';
import {
  calculateFinalPrice,
  type CouponDiscount,
  type PriceBreakdown,
} from '@/shared/lib/pricing';

type ConfigResponse = { stripePublishableKey: string | null; environment: string };
type CouponValidateResponse =
  | {
      valid: true;
      couponId: string;
      discount: { type: 'percentage' | 'fixed'; value: number };
      breakdown: PriceBreakdown | null;
    }
  | { valid: false; reason: string };
type CheckoutResponse =
  | {
      clientSecret: string;
      amount: number;
      breakdown: PriceBreakdown & { taxCents: number };
    }
  | { freeAccess: true; enrollmentId: string };

const stripePromiseCache = new Map<string, Promise<StripeType | null>>();
function getStripePromise(publishableKey: string): Promise<StripeType | null> {
  let p = stripePromiseCache.get(publishableKey);
  if (!p) {
    p = loadStripe(publishableKey);
    stripePromiseCache.set(publishableKey, p);
  }
  return p;
}

export function Checkout() {
  const { t } = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const courseSlug = searchParams.get('course') ?? '';

  const { data: me, isLoading: meLoading } = useMe();

  const config = useQuery({
    queryKey: ['config'],
    queryFn: () => api<ConfigResponse>('/api/v1/config'),
  });

  const courseQuery = useQuery({
    queryKey: ['public', 'course', courseSlug],
    queryFn: () => api<PublicCourseDetailResponse>(`/api/v1/courses/${courseSlug}`),
    enabled: Boolean(courseSlug),
    retry: false,
  });

  useEffect(() => {
    document.title = `${t('checkout.page_title')} | Akadémia Dajany Rodriguez`;
  }, [t]);

  if (!meLoading && !me) {
    const target = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/${locale}/prihlasenie?redirectTo=${target}`} replace />;
  }

  if (!courseSlug) {
    return <MissingCourseFallback locale={locale} />;
  }

  if (config.isLoading || courseQuery.isLoading) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-text-secondary">
        <div className="inline-flex items-center gap-3">
          <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
          {t('common.loading')}
        </div>
      </div>
    );
  }

  if (courseQuery.isError) {
    if (courseQuery.error instanceof ApiError && courseQuery.error.status === 404) {
      return <MissingCourseFallback locale={locale} />;
    }
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  if (!courseQuery.data || !config.data?.stripePublishableKey) {
    return (
      <div className="px-6 py-32 text-center font-sans text-body text-error">
        {t('errors.generic')}
      </div>
    );
  }

  return (
    <CheckoutInner
      locale={locale}
      course={courseQuery.data.course}
      stripePromise={getStripePromise(config.data.stripePublishableKey)}
    />
  );
}

function MissingCourseFallback({ locale }: { locale: Locale }) {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-2xl px-6 py-32 text-center">
      <div className="dr-card-static p-10">
        <h1 className="font-display text-h2 text-primary-dark">{t('checkout.missing_course')}</h1>
        <Link
          to={`/${locale}/kurzy`}
          className="mt-6 inline-flex items-center gap-2 font-sans text-body text-primary hover:gap-3 transition-all"
        >
          <ArrowLeft size={16} /> {t('courses.page_title')}
        </Link>
      </div>
    </div>
  );
}

type CheckoutInnerProps = {
  locale: Locale;
  course: PublicCourseDetailResponse['course'];
  stripePromise: Promise<StripeType | null>;
};

function CheckoutInner({ locale, course, stripePromise }: CheckoutInnerProps) {
  const { t } = useTranslation();

  const baseBreakdown: PriceBreakdown = useMemo(
    () =>
      calculateFinalPrice({
        priceCents: course.priceCents,
        launchPriceCents: course.launchPriceCents,
        launchSeatsTotal: course.launchSeatsTotal,
        launchSeatsUsed: course.launchSeatsTotal - course.launchSeatsRemaining,
        coupon: null,
      }),
    [course.priceCents, course.launchPriceCents, course.launchSeatsTotal, course.launchSeatsRemaining],
  );

  const [appliedCoupon, setAppliedCoupon] = useState<{
    code: string;
    discount: CouponDiscount;
    breakdown: PriceBreakdown;
  } | null>(null);

  const breakdown = appliedCoupon?.breakdown ?? baseBreakdown;

  // mode: 'payment' lets us mount Elements before we have a PaymentIntent.
  // paymentMethodTypes pinned to ['card'] to match Invoice payment_settings.
  // Theme tokens drive Stripe Elements to feel native to the glass surface.
  const elementsOptions = useMemo(
    () => ({
      mode: 'payment' as const,
      amount: Math.max(breakdown.totalCents, 50),
      currency: course.currency.toLowerCase(),
      paymentMethodCreation: 'manual' as const,
      paymentMethodTypes: ['card'],
      locale: locale === 'cs' ? ('cs' as const) : ('sk' as const),
      appearance: {
        theme: 'stripe' as const,
        variables: {
          colorPrimary: '#753BBD',
          colorBackground: '#ffffff',
          colorText: '#1a1025',
          colorTextSecondary: '#6b5a7a',
          colorDanger: '#c4322d',
          fontFamily: '"DM Sans", system-ui, sans-serif',
          borderRadius: '14px',
          spacingUnit: '4px',
        },
        rules: {
          '.Input': {
            border: '1px solid rgba(117, 59, 189, 0.18)',
            boxShadow: 'none',
            backgroundColor: 'rgba(255, 255, 255, 0.85)',
          },
          '.Input:focus': {
            border: '1px solid #753BBD',
            boxShadow: '0 0 0 3px rgba(117, 59, 189, 0.15)',
          },
        },
      },
    }),
    [breakdown.totalCents, course.currency, locale],
  );

  return (
    <article>
      <div className="mx-auto max-w-6xl px-5 sm:px-6 lg:px-8 py-10 sm:py-14 lg:py-16">
        <Link
          to={`/${locale}/kurzy/${course.slug}`}
          className="inline-flex items-center gap-2 font-sans text-body-small text-primary hover:gap-3 transition-all"
        >
          <ArrowLeft size={14} /> {t('checkout.back_to_course')}
        </Link>

        <div className="mt-6 mb-10 max-w-2xl">
          <p className="dr-eyebrow">{t('checkout.eyebrow', { defaultValue: 'Bezpečná platba' })}</p>
          <h1 className="mt-3 font-display text-h1 text-primary-dark">
            {t('checkout.page_title')}
          </h1>
        </div>

        <div className="grid gap-8 lg:gap-10 lg:grid-cols-[1fr_22rem]">
          {/* Main column */}
          <div className="min-w-0">
            <Elements stripe={stripePromise} options={elementsOptions}>
              <CheckoutForm
                locale={locale}
                course={course}
                breakdown={breakdown}
                couponCode={appliedCoupon?.code ?? null}
              />
            </Elements>
          </div>

          {/* Sticky summary */}
          <aside className="lg:sticky lg:top-24 self-start space-y-5">
            <CheckoutSummary
              course={course}
              breakdown={breakdown}
              couponCode={appliedCoupon?.code ?? null}
            />
            <CouponSection
              courseId={course.id}
              baseBreakdown={baseBreakdown}
              appliedCoupon={appliedCoupon}
              onApplied={(applied) => setAppliedCoupon(applied)}
              onRemoved={() => setAppliedCoupon(null)}
            />
            <TrustBlock />
          </aside>
        </div>
      </div>
    </article>
  );
}

function CheckoutSummary({
  course,
  breakdown,
  couponCode,
}: {
  course: PublicCourseDetailResponse['course'];
  breakdown: PriceBreakdown;
  couponCode: string | null;
}) {
  const { t } = useTranslation();
  const fmt = (cents: number) =>
    new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: course.currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);
  return (
    <section className="dr-card-static p-6">
      <h2 className="font-display text-h4 text-primary-dark">{t('checkout.summary_title')}</h2>
      <div className="mt-4 flex items-start gap-3.5">
        {course.thumbnailUrl ? (
          <img
            src={course.thumbnailUrl}
            alt={course.title}
            className="h-16 w-20 rounded-md object-cover border border-glass-border"
          />
        ) : (
          <div className="h-16 w-20 rounded-md bg-gradient-to-br from-primary-light/55 to-primary/35 border border-glass-border" />
        )}
        <div className="flex-1 min-w-0">
          <p className="font-display text-body-large text-primary-dark leading-tight">
            {course.title}
          </p>
          {breakdown.wasLaunchPrice && (
            <span
              className="mt-1.5 inline-flex items-center gap-1 rounded-pill px-2.5 py-0.5 font-sans text-caption font-medium text-white"
              style={{ background: 'linear-gradient(135deg, #753bbd, #512d6d)' }}
            >
              <Sparkles size={10} />
              {t('checkout.summary_launch_badge')}
            </span>
          )}
        </div>
      </div>
      <dl className="mt-5 space-y-2 font-sans text-body-small">
        <div className="flex justify-between">
          <dt className="text-text-secondary">{t('checkout.summary_base')}</dt>
          <dd className="text-primary-dark font-medium">{fmt(breakdown.basePriceCents)}</dd>
        </div>
        {breakdown.discountCents > 0 && (
          <div className="flex justify-between">
            <dt className="text-text-secondary">
              {t('checkout.summary_discount')}
              {couponCode && <span className="ml-1 text-primary font-medium">({couponCode})</span>}
            </dt>
            <dd className="text-success font-medium">−{fmt(breakdown.discountCents)}</dd>
          </div>
        )}
        <div className="flex justify-between border-t border-glass-border-strong pt-3 mt-3">
          <dt className="font-display text-h4 text-primary-dark">{t('checkout.summary_total')}</dt>
          <dd className="font-display text-h4 text-primary-dark">{fmt(breakdown.totalCents)}</dd>
        </div>
        <p className="font-sans text-caption text-text-muted">{t('checkout.summary_tax_included')}</p>
      </dl>
    </section>
  );
}

function CouponSection({
  courseId,
  baseBreakdown,
  appliedCoupon,
  onApplied,
  onRemoved,
}: {
  courseId: string;
  baseBreakdown: PriceBreakdown;
  appliedCoupon: { code: string; discount: CouponDiscount; breakdown: PriceBreakdown } | null;
  onApplied: (applied: { code: string; discount: CouponDiscount; breakdown: PriceBreakdown }) => void;
  onRemoved: () => void;
}) {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  async function apply(e: React.FormEvent) {
    e.preventDefault();
    if (!code.trim()) return;
    setError(null);
    setPending(true);
    try {
      const response = await api<CouponValidateResponse>('/api/v1/coupons/validate', {
        method: 'POST',
        body: { code: code.trim(), courseId },
      });
      if (response.valid) {
        const applied: CouponDiscount = {
          type: response.discount.type,
          value: response.discount.value,
        };
        onApplied({
          code: code.trim().toUpperCase(),
          discount: applied,
          breakdown: response.breakdown ?? {
            ...baseBreakdown,
            discountCents:
              baseBreakdown.basePriceCents - applyJsCoupon(baseBreakdown.basePriceCents, applied),
            totalCents: applyJsCoupon(baseBreakdown.basePriceCents, applied),
          },
        });
        setCode('');
      } else {
        setError(t(`checkout.coupon_invalid_${response.reason}`));
      }
    } catch {
      setError(t('errors.generic'));
    } finally {
      setPending(false);
    }
  }

  if (appliedCoupon) {
    return (
      <section
        className="rounded-2xl p-4 flex items-center justify-between gap-3"
        style={{
          background: 'linear-gradient(135deg, rgba(45,139,85,0.10), rgba(45,139,85,0.04))',
          border: '1px solid rgba(45,139,85,0.25)',
        }}
      >
        <p className="font-sans text-body-small text-success inline-flex items-center gap-2">
          <CheckCircle2 size={16} />
          {t('checkout.coupon_applied', { code: appliedCoupon.code })}
        </p>
        <button
          type="button"
          onClick={onRemoved}
          aria-label={t('checkout.coupon_remove')}
          className="inline-flex h-8 w-8 items-center justify-center rounded-pill text-success/70 hover:bg-success/10 hover:text-success transition-colors"
        >
          <X size={14} />
        </button>
      </section>
    );
  }

  return (
    <section className="dr-card-static p-5">
      <h3 className="font-display text-h4 text-primary-dark inline-flex items-center gap-2">
        <BadgePercent size={18} className="text-primary" />
        {t('checkout.coupon_title')}
      </h3>
      <form className="mt-3 flex flex-wrap gap-2" onSubmit={apply}>
        <input
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder={t('checkout.coupon_placeholder')}
          className="flex-1 min-w-0 h-10 rounded-pill bg-white/80 backdrop-blur-md border border-glass-border-strong px-4 font-sans text-body-small uppercase placeholder:normal-case placeholder:text-text-muted focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow transition-all"
          autoComplete="off"
          spellCheck={false}
        />
        <Button type="submit" variant="primary" size="sm" disabled={pending || !code.trim()}>
          {t('checkout.coupon_apply')}
        </Button>
      </form>
      {error && <p className="mt-2 font-sans text-body-small text-error">{error}</p>}
    </section>
  );
}

function TrustBlock() {
  const { t } = useTranslation();
  return (
    <div className="rounded-2xl p-4 bg-white/40 backdrop-blur-md border border-glass-border">
      <ul className="space-y-2 font-sans text-caption text-text-secondary">
        <li className="inline-flex items-center gap-2">
          <Lock size={12} className="text-primary" />
          {t('checkout.trust_secure', { defaultValue: 'Šifrované Stripe spojenie' })}
        </li>
        <li className="inline-flex items-center gap-2">
          <CheckCircle2 size={12} className="text-success" />
          {t('checkout.trust_invoice', { defaultValue: 'Faktúra na e-mail po platbe' })}
        </li>
        <li className="inline-flex items-center gap-2">
          <Sparkles size={12} className="text-primary" />
          {t('checkout.trust_access', { defaultValue: 'Doživotný prístup k obsahu' })}
        </li>
      </ul>
    </div>
  );
}

function applyJsCoupon(amount: number, c: CouponDiscount): number {
  if (c.type === 'percentage') return Math.max(0, Math.round(amount * (1 - c.value / 100)));
  return Math.max(0, amount - Math.max(0, c.value));
}

type BillingFormState = {
  type: 'individual' | 'company';
  name: string;
  email: string;
  phone: string;
  companyName: string;
  ico: string;
  dic: string;
  icDph: string;
  street: string;
  city: string;
  zip: string;
  country: string;
};

function CheckoutForm({
  locale,
  course,
  breakdown,
  couponCode,
}: {
  locale: Locale;
  course: PublicCourseDetailResponse['course'];
  breakdown: PriceBreakdown;
  couponCode: string | null;
}) {
  const { t } = useTranslation();
  const stripe = useStripe();
  const elements = useElements();
  const navigate = useNavigate();

  const [billing, setBilling] = useState<BillingFormState>({
    type: 'individual',
    name: '',
    email: '',
    phone: '',
    companyName: '',
    ico: '',
    dic: '',
    icDph: '',
    street: '',
    city: '',
    zip: '',
    country: 'SK',
  });
  const [consent, setConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const idempotencyKeyRef = useRef<string>(crypto.randomUUID());

  const isFree = breakdown.totalCents === 0;

  function handleChange<K extends keyof BillingFormState>(key: K, value: BillingFormState[K]) {
    setBilling((b) => ({ ...b, [key]: value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setErrorMsg(null);
    if (!consent) return;
    setSubmitting(true);

    try {
      if (!isFree) {
        if (!stripe || !elements) {
          setErrorMsg(t('errors.generic'));
          return;
        }
        const submitResult = await elements.submit();
        if (submitResult.error) {
          setErrorMsg(submitResult.error.message ?? t('checkout.error_payment_failed'));
          return;
        }
      }

      const body = {
        couponCode: couponCode ?? undefined,
        digitalConsent: true as const,
        billingData:
          billing.type === 'individual'
            ? {
                type: 'individual' as const,
                name: billing.name,
                email: billing.email,
                phone: billing.phone || undefined,
                address: {
                  street: billing.street,
                  city: billing.city,
                  zip: billing.zip,
                  country: billing.country,
                },
              }
            : {
                type: 'company' as const,
                name: billing.name,
                email: billing.email,
                phone: billing.phone || undefined,
                companyName: billing.companyName,
                ico: billing.ico || undefined,
                dic: billing.dic || undefined,
                icDph: billing.icDph || undefined,
                address: {
                  street: billing.street,
                  city: billing.city,
                  zip: billing.zip,
                  country: billing.country,
                },
              },
      };

      const response = await fetch(`/api/v1/courses/${course.id}/checkout`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Idempotency-Key': idempotencyKeyRef.current,
        },
        body: JSON.stringify(body),
      });

      if (response.status === 409) {
        setErrorMsg(t('checkout.error_already_owned'));
        return;
      }
      if (!response.ok) {
        setErrorMsg(t('checkout.error_unknown'));
        return;
      }

      const json = (await response.json()) as { data: CheckoutResponse };
      const data = json.data;

      if ('freeAccess' in data && data.freeAccess) {
        navigate(`/${locale}/portal/kurzy/${course.slug}?welcome=1`, { replace: true });
        return;
      }

      const paid = data as Extract<CheckoutResponse, { clientSecret: string }>;
      if (!stripe || !elements) {
        setErrorMsg(t('errors.generic'));
        return;
      }
      const result = await stripe.confirmPayment({
        elements,
        clientSecret: paid.clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/${locale}/portal/kurzy/${course.slug}?welcome=1`,
        },
        redirect: 'if_required',
      });
      if (result.error) {
        setErrorMsg(result.error.message ?? t('checkout.error_payment_failed'));
        return;
      }
      navigate(`/${locale}/portal/kurzy/${course.slug}?welcome=1`, { replace: true });
    } catch {
      setErrorMsg(t('checkout.error_unknown'));
    } finally {
      setSubmitting(false);
    }
  }

  const fmt = (cents: number) =>
    new Intl.NumberFormat('sk-SK', {
      style: 'currency',
      currency: course.currency,
      minimumFractionDigits: 2,
    }).format(cents / 100);

  return (
    <form className="space-y-6" onSubmit={handleSubmit} noValidate>
      <section className="dr-card-static p-6 sm:p-8">
        <h2 className="font-display text-h3 text-primary-dark">{t('checkout.billing_title')}</h2>
        <p className="mt-1 font-sans text-body-small text-text-secondary">
          {t('checkout.billing_subtitle', { defaultValue: 'Tieto údaje sa použijú na faktúru.' })}
        </p>

        <div className="mt-5 flex flex-wrap gap-2 p-1.5 rounded-pill bg-white/60 backdrop-blur-md border border-glass-border-strong w-fit">
          {(['individual', 'company'] as const).map((type) => (
            <button
              key={type}
              type="button"
              className={cn(
                'rounded-pill px-4 py-1.5 font-sans text-body-small font-medium transition-all',
                billing.type === type
                  ? 'bg-gradient-to-br from-primary to-primary-dark text-white shadow-[0_2px_8px_rgba(117,59,189,0.25)]'
                  : 'text-text-secondary hover:text-primary',
              )}
              onClick={() => handleChange('type', type)}
            >
              {t(`checkout.billing_type_${type}`)}
            </button>
          ))}
        </div>

        <div className="mt-6 grid gap-4 md:grid-cols-2">
          <Field
            label={t('checkout.billing_name_label')}
            value={billing.name}
            onChange={(v) => handleChange('name', v)}
            required
            autoComplete="name"
          />
          <Field
            label={t('checkout.billing_email_label')}
            value={billing.email}
            onChange={(v) => handleChange('email', v)}
            type="email"
            required
            autoComplete="email"
          />
          <Field
            label={t('checkout.billing_phone_label')}
            value={billing.phone}
            onChange={(v) => handleChange('phone', v)}
            type="tel"
            autoComplete="tel"
          />
          {billing.type === 'company' && (
            <>
              <Field
                label={t('checkout.billing_company_name_label')}
                value={billing.companyName}
                onChange={(v) => handleChange('companyName', v)}
                required
                autoComplete="organization"
              />
              <Field
                label={t('checkout.billing_ico_label')}
                value={billing.ico}
                onChange={(v) => handleChange('ico', v)}
              />
              <Field
                label={t('checkout.billing_dic_label')}
                value={billing.dic}
                onChange={(v) => handleChange('dic', v)}
              />
              <Field
                label={t('checkout.billing_ic_dph_label')}
                value={billing.icDph}
                onChange={(v) => handleChange('icDph', v)}
                placeholder="SK1234567890"
              />
            </>
          )}
          <Field
            label={t('checkout.billing_address_street_label')}
            value={billing.street}
            onChange={(v) => handleChange('street', v)}
            required
            autoComplete="address-line1"
            className="md:col-span-2"
          />
          <Field
            label={t('checkout.billing_address_city_label')}
            value={billing.city}
            onChange={(v) => handleChange('city', v)}
            required
            autoComplete="address-level2"
          />
          <Field
            label={t('checkout.billing_address_zip_label')}
            value={billing.zip}
            onChange={(v) => handleChange('zip', v)}
            required
            autoComplete="postal-code"
          />
          <Field
            label={t('checkout.billing_address_country_label')}
            value={billing.country}
            onChange={(v) => handleChange('country', v.toUpperCase().slice(0, 2))}
            required
            autoComplete="country"
          />
        </div>
      </section>

      {!isFree && (
        <section className="dr-card-static p-6 sm:p-8">
          <h2 className="font-display text-h3 text-primary-dark inline-flex items-center gap-2">
            <Lock size={18} className="text-primary" />
            {t('checkout.payment_title')}
          </h2>
          <p className="mt-1 font-sans text-body-small text-text-secondary">
            {t('checkout.payment_subtitle', { defaultValue: 'Tvoja platba je spracovaná cez Stripe.' })}
          </p>
          <div className="mt-5">
            <PaymentElement options={{ layout: 'tabs' }} />
          </div>
        </section>
      )}

      <label
        className={cn(
          'flex items-start gap-3 rounded-2xl p-4 cursor-pointer transition-all',
          consent
            ? 'bg-primary-50/80 border border-primary/25'
            : 'bg-white/70 border border-glass-border-strong hover:border-primary/25',
        )}
      >
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          required
          className="mt-0.5 h-5 w-5 accent-primary cursor-pointer"
        />
        <span className="font-sans text-body-small text-text-secondary leading-relaxed">
          {t('checkout.consent_label')}
        </span>
      </label>

      {errorMsg && (
        <p
          className="rounded-2xl px-4 py-3 font-sans text-body-small text-error border"
          style={{
            background: 'rgba(196,50,45,0.08)',
            borderColor: 'rgba(196,50,45,0.25)',
          }}
        >
          {errorMsg}
        </p>
      )}

      <Button type="submit" size="lg" disabled={!consent || submitting} className="w-full">
        {submitting
          ? t('checkout.submit_processing')
          : isFree
            ? t('checkout.submit_free')
            : t('checkout.submit', { amount: fmt(breakdown.totalCents) })}
      </Button>

      <p className="text-center font-sans text-caption text-text-muted">
        <span
          dangerouslySetInnerHTML={{
            __html: t('checkout.footer_terms', {
              terms: `<a href="/${locale}/obchodne-podmienky" class="text-primary hover:underline">${t(
                'checkout.footer_terms_link',
              )}</a>`,
              privacy: `<a href="/${locale}/ochrana-sukromia" class="text-primary hover:underline">${t(
                'checkout.footer_privacy_link',
              )}</a>`,
              interpolation: { escapeValue: false },
            }),
          }}
        />
      </p>
    </form>
  );
}

function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  required?: boolean;
  autoComplete?: string;
  placeholder?: string;
  className?: string;
}) {
  return (
    <label className={cn('flex flex-col gap-1.5', props.className)}>
      <span className="font-sans text-body-small font-medium text-text-secondary">
        {props.label}
        {props.required && <span className="ml-0.5 text-primary/70">*</span>}
      </span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        required={props.required}
        autoComplete={props.autoComplete}
        placeholder={props.placeholder}
        className="h-11 rounded-md bg-white/80 backdrop-blur-md border border-glass-border-strong px-4 font-sans text-body text-text-primary placeholder:text-text-muted hover:border-primary/30 focus-visible:outline-none focus-visible:border-primary focus-visible:shadow-glow focus-visible:bg-white transition-all"
      />
    </label>
  );
}
